import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, unwrapLookupItems,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const STOCK_DOCS_API = '/api/stockdocuments';

const STATUS_OPTIONS = [
  { value: 0, label: 'Taslak' },
  { value: 1, label: 'İşlendi' },
];
const statusLabel = (value) => {
  const found = STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : (value === null || value === undefined || value === '' ? '-' : String(value));
};
const statusTone = (value) => (Number(value) === 1 ? 'ok' : 'neutral');

const COLUMN_FILTER_META = {
  documentDate: { type: 'text', odataField: 'DocumentDate' },
  description: { type: 'text', odataField: 'Description' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['Description'];

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function formatNumber(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}

export function WarehouseStockResetListPage() {
  usePageMeta('Depo Stok Sıfırlama', 'Depo stok sıfırlama geçmişini görüntüleyin.');

  const navigate = useNavigate();
  const { canAdd } = usePageActions();
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [viewModal, setViewModal] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: STOCK_DOCS_API,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    baseFilter: `DocumentType eq 2 and contains(Description, 'Tarihli Stock Sıfırlama')`,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  useEffect(() => {
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setVendors(unwrapLookupItems(d)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/warehouses/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((w) => setWarehouses(unwrapLookupItems(w)))
      .catch(() => {});
  }, []);

  const vendorLabel = useCallback((id) => {
    if (!id) return '-';
    const d = vendors.find((x) => x.id === id);
    if (!d) return '-';
    return d.name ? `${d.textId || ''} - ${d.name}`.replace(/^ - /, '') : (d.textId || '-');
  }, [vendors]);

  const warehouseLabel = useCallback((id) => {
    if (!id) return '-';
    const w = warehouses.find((x) => x.id === id);
    if (!w) return '-';
    return w.name ? `${w.textId || ''} - ${w.name}`.replace(/^ - /, '') : (w.textId || '-');
  }, [warehouses]);

  const openView = useCallback(async (row) => {
    setViewModal({ header: row, lines: [] });
    setViewLoading(true);
    try {
      const res = await fetch(`${STOCK_DOCS_API}/GetById?Id=${row.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Belge alınamadı');
      const body = await res.json();
      const doc = body?.data ?? body;
      setViewModal({ header: { ...row, ...doc }, lines: doc?.lines || [] });
    } catch (e) {
      showToast('error', e.message || 'Belge görüntülenemedi');
    } finally {
      setViewLoading(false);
    }
  }, []);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'vendor', header: 'Distribütör',
      cell: ({ row }) => vendorLabel(row.original.vendorId),
    }),
    columnHelper.display({
      id: 'warehouse', header: 'Depo',
      cell: ({ row }) => warehouseLabel(row.original.warehouseId),
    }),
    columnHelper.accessor('documentDate', {
      header: 'Tarih', meta: { filterType: 'text' },
      cell: (info) => displayDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'productCount', header: 'Ürün Sayısı',
      cell: ({ row }) => row.original.lineCount || 0,
    }),
    columnHelper.display({
      id: 'totalQuantity', header: 'Toplam Miktar',
      cell: ({ row }) => formatNumber(row.original.totalQuantity || 0),
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); navigate(`/warehouse-stock-reset/${row.original.id}`); }}><ViewIcon /></button>
        </span>
      ),
    }),
  ], [vendorLabel, warehouseLabel, navigate]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Distribütör', valueGetter: ({ row }) => vendorLabel(row.vendorId) },
        { headerName: 'Depo', valueGetter: ({ row }) => warehouseLabel(row.warehouseId) },
        { headerName: 'Tarih', valueGetter: ({ row }) => displayDate(row.documentDate) },
        { headerName: 'Ürün Sayısı', valueGetter: ({ row }) => row.lineCount || 0 },
        { headerName: 'Toplam Miktar', valueGetter: ({ row }) => formatNumber(row.totalQuantity || 0) },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `depo-stok-sifirla_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={() => navigate('/warehouse-stock-reset/new')}>+ Yeni Sıfırlama</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          onRowDoubleClick={() => {}}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {viewModal && (
        <div style={s.overlay} onClick={() => setViewModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Depo Stok Sıfırlama Detay</h3>
              <button style={s.closeBtn} onClick={() => setViewModal(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              {viewLoading ? (
                <div style={{ padding: 24 }}>Yükleniyor...</div>
              ) : (
                <>
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Distribütör</label>
                      <input style={s.inputView} readOnly value={vendorLabel(viewModal.header?.vendorId)} />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Depo</label>
                      <input style={s.inputView} readOnly value={warehouseLabel(viewModal.header?.warehouseId)} />
                    </div>
                  </div>
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Tarih</label>
                      <input style={s.inputView} readOnly value={displayDate(viewModal.header?.documentDate)} />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Durum</label>
                      <input style={s.inputView} readOnly value={statusLabel(viewModal.header?.status)} />
                    </div>
                  </div>
                  <div style={s.formRow}>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Açıklama</label>
                      <textarea style={{ ...s.inputView, minHeight: 60, resize: 'vertical' }} readOnly value={viewModal.header?.description || '-'} />
                    </div>
                  </div>
                  {viewModal.lines && viewModal.lines.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <label style={s.label}>Sıfırlanan Ürünler ({viewModal.lines.length})</label>
                      <table style={{
                        width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 13,
                        borderTop: '1px solid var(--border-color, #e5e7eb)',
                      }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-secondary, #f9fafb)' }}>
                            <th style={{ padding: 8, textAlign: 'left' }}>Ürün ID</th>
                            <th style={{ padding: 8, textAlign: 'left' }}>Birim</th>
                            <th style={{ padding: 8, textAlign: 'right' }}>Miktar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewModal.lines.map((line, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                              <td style={{ padding: 8 }}>{line.productId}</td>
                              <td style={{ padding: 8 }}>{line.unitName || '-'}</td>
                              <td style={{ padding: 8, textAlign: 'right' }}>{formatNumber(line.quantity)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setViewModal(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
